// 会员套餐 — 会员档位 (周/月/年 + 权益包专享档) 的配置页。
//
// 会员档 = kind=subscription 的 SKU (复用商业 SKU 接口, 无新表):
//   diamond_amount = 每周期赠钻批次量 (批次随会员到期 + 7 天宽限作废)
//   cycle_days     = 周期 (7/30/365)
//   status         = draft(前台隐藏, 仍可被权益包授予) | active(前台可售) | archived
// 2026-08-11 起有「权益包专享档」(membership_monthly_bundle 400钻, draft):
// 前台订阅列表只显示 active, 授予路径 GetSKUByCode 不过滤状态 — draft 即"内部档"。

import { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Form,
  Input,
  InputNumber,
  Modal,
  Popover,
  Select,
  Space,
  Table,
  Tag,
  Typography,
  message,
} from 'antd';
import { CrownOutlined, PlusOutlined } from '@ant-design/icons';
import { listApps, type App } from '@/api/apps';
import { listSKUs, createSKU, updateSKU, type SKU, type SKUStatus } from '@/api/skus';
import { formatCNY } from '@/utils/format';

const { Text } = Typography;

const STATUS_META: Record<string, { color: string; label: string; hint: string }> = {
  active: { color: 'green', label: '在售', hint: '前台订阅页可见可购' },
  draft: { color: 'orange', label: '内部档', hint: '前台隐藏; 权益包/后台授予仍可用' },
  archived: { color: 'default', label: '已下架', hint: '不可见' },
};

function cycleLabel(days: number): string {
  if (days === 7) return '周卡';
  if (days >= 28 && days <= 31) return '月卡';
  if (days >= 360) return '年卡';
  return `${days} 天`;
}

export function MembershipPlansPage() {
  const [apps, setApps] = useState<App[]>([]);
  const [appId, setAppId] = useState<number | undefined>(undefined);
  const [rows, setRows] = useState<SKU[]>([]);
  const [loading, setLoading] = useState(false);

  const [editing, setEditing] = useState<SKU | null>(null);
  const [creating, setCreating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm();

  useEffect(() => {
    listApps({ page_size: 100 })
      .then((r) => {
        setApps(r.data);
        const first = r.data.find((a) => a.status === 'active') ?? r.data[0];
        if (first) setAppId((prev) => prev ?? first.id);
      })
      .catch(() => message.error('加载 App 列表失败'));
  }, []);

  const load = useCallback(() => {
    if (!appId) return;
    setLoading(true);
    listSKUs({ app_id: appId, kind: 'subscription' })
      .then((r) => setRows(r.data))
      .catch((e) => message.error(e instanceof Error ? e.message : '加载失败'))
      .finally(() => setLoading(false));
  }, [appId]);
  useEffect(load, [load]);

  const openEdit = (sku: SKU | null) => {
    setEditing(sku);
    setCreating(!sku);
    form.setFieldsValue(
      sku
        ? {
            code: sku.code,
            name: sku.name,
            price_yuan: sku.base_price_cents / 100,
            diamond_amount: sku.diamond_amount,
            cycle_days: sku.cycle_days,
            status: sku.status,
          }
        : { code: '', name: '', price_yuan: undefined, diamond_amount: 0, cycle_days: 30, status: 'draft' },
    );
  };

  const onSave = async () => {
    const v = await form.validateFields();
    if (!appId) return;
    setSaving(true);
    try {
      const input = {
        app_id: appId,
        code: v.code,
        name: v.name,
        kind: 'subscription' as const,
        base_price_cents: Math.round(Number(v.price_yuan) * 100),
        diamond_amount: Number(v.diamond_amount) || 0,
        cycle_days: Number(v.cycle_days) || 30,
        auto_renew: false,
        status: v.status as SKUStatus,
      };
      if (editing) {
        await updateSKU(editing.id, input);
        message.success('已保存');
      } else {
        await createSKU(input);
        message.success('已创建');
      }
      setEditing(null);
      setCreating(false);
      load();
    } catch (e) {
      message.error(e instanceof Error ? e.message : '保存失败');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <Space wrap>
        <CrownOutlined style={{ fontSize: 18 }} />
        <Text strong style={{ fontSize: 16 }}>会员套餐</Text>
        <Select
          style={{ width: 260 }}
          value={appId}
          options={apps.map((a) => ({ value: a.id, label: `#${a.id} ${a.name} (${a.slug})` }))}
          onChange={setAppId}
          showSearch
          optionFilterProp="label"
        />
        <Button icon={<PlusOutlined />} onClick={() => openEdit(null)}>
          新建档位
        </Button>
        <Button onClick={load}>刷新</Button>
      </Space>

      <Alert
        type="info"
        showIcon
        message="会员档 = 订阅类 SKU。diamond_amount = 每周期赠钻(批次随会员到期+7天宽限作废)。状态「内部档(draft)」前台隐藏但可被权益包授予 — 权益包专享档就用它。渠道差异定价在「商业 SKU」页配置。"
      />

      <Table
        rowKey="id"
        size="middle"
        loading={loading}
        dataSource={rows}
        pagination={false}
        columns={[
          { title: 'ID', dataIndex: 'id', width: 60 },
          { title: 'Code', dataIndex: 'code', render: (v: string) => <Text code>{v}</Text> },
          { title: '名称', dataIndex: 'name' },
          {
            title: '档位',
            dataIndex: 'cycle_days',
            width: 90,
            render: (d: number) => <Tag color="blue">{cycleLabel(d)}</Tag>,
          },
          {
            title: '单卖价',
            dataIndex: 'base_price_cents',
            width: 100,
            render: (c: number) => formatCNY(c),
          },
          {
            title: '每周期赠钻',
            dataIndex: 'diamond_amount',
            width: 110,
            render: (n: number) => (n > 0 ? <Text strong>💎 {n}</Text> : <Text type="secondary">不赠</Text>),
          },
          {
            title: '状态',
            dataIndex: 'status',
            width: 110,
            render: (s: string) => {
              const m = STATUS_META[s] ?? { color: 'default', label: s, hint: '' };
              return (
                <Popover content={m.hint}>
                  <Tag color={m.color}>{m.label}</Tag>
                </Popover>
              );
            },
          },
          {
            title: '操作',
            width: 80,
            render: (_: unknown, r: SKU) => (
              <Button size="small" type="link" onClick={() => openEdit(r)}>
                编辑
              </Button>
            ),
          },
        ]}
      />

      <Modal
        title={creating ? '新建会员档位' : `编辑 · ${editing?.code}`}
        open={creating || !!editing}
        onOk={onSave}
        okText="保存"
        confirmLoading={saving}
        onCancel={() => {
          setEditing(null);
          setCreating(false);
        }}
        destroyOnClose
      >
        <Form form={form} layout="vertical">
          <Form.Item name="code" label="Code (唯一, 建议 membership_*)" rules={[{ required: true }]}>
            <Input disabled={!creating} placeholder="membership_monthly_xxx" />
          </Form.Item>
          <Form.Item name="name" label="名称" rules={[{ required: true }]}>
            <Input placeholder="写真月会员(权益包专享·400钻)" />
          </Form.Item>
          <Space size="large" wrap>
            <Form.Item name="price_yuan" label="单卖价 (元)" rules={[{ required: true }]}>
              <InputNumber min={0.01} step={0.1} style={{ width: 140 }} addonAfter="元" />
            </Form.Item>
            <Form.Item name="diamond_amount" label="每周期赠钻">
              <InputNumber min={0} step={50} style={{ width: 140 }} addonAfter="💎" />
            </Form.Item>
            <Form.Item name="cycle_days" label="周期 (天)">
              <Select
                style={{ width: 140 }}
                options={[
                  { value: 7, label: '7 (周卡)' },
                  { value: 30, label: '30 (月卡)' },
                  { value: 365, label: '365 (年卡)' },
                ]}
              />
            </Form.Item>
          </Space>
          <Form.Item name="status" label="状态">
            <Select
              style={{ width: 260 }}
              options={[
                { value: 'active', label: '在售 (前台可见可购)' },
                { value: 'draft', label: '内部档 (前台隐藏, 权益包可授予)' },
                { value: 'archived', label: '下架' },
              ]}
            />
          </Form.Item>
        </Form>
      </Modal>
    </Space>
  );
}
